import { useState, useEffect } from 'react';

const STORAGE_KEY = 'LathiNyx_clients_v2';

const EMPTY_CLIENT = {
  id: '', name: '', email: '', phone: '', city: '', company: '',
  svc: 'Web Dev', status: 'Active', notes: '', clientId: '',
  projects: [], invoices: [], contracts: [], files: [],
  createdAt: '',
};

function loadClients() { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch { return []; } }
function saveClients(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  // Notify other components (Projects, Overview) that clients changed
  window.dispatchEvent(new Event('storage'));
}
function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2); }
function initials(name) { return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase(); }
const AV_COLORS = ['#2563eb','#7c3aed','#e11d48','#16a34a','#d97706','#0891b2'];
function avColor(id) { return AV_COLORS[id.charCodeAt(0) % AV_COLORS.length]; }

const STATUS_COLORS = { Active: '#16a34a', Inactive: '#6b7280', Pending: '#d97706', Completed: '#2563eb' };
const STATUS_BG = { Active: '#f0fdf4', Inactive: '#f3f4f6', Pending: '#fffbeb', Completed: '#eff6ff' };

function fmtAmt(n) { return '₹' + (parseFloat(n) || 0).toLocaleString('en-IN'); }
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  return diff;
}
function daysLabel(d) {
  if (d === null) return '—';
  if (d < 0) return `${Math.abs(d)}d overdue`;
  if (d === 0) return 'Due today';
  return `Due in ${d}d`;
}

// ── Payment Summary for a client ──────────────────────────────────────────
function PaymentSummary({ client }) {
  const invoices = client.invoices || [];
  const totalInvoiced = invoices.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalPaid = invoices.filter(i => i.status === 'Paid').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalDue = invoices.filter(i => i.status === 'Pending' || i.status === 'Overdue').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const overdue = invoices.filter(i => i.status === 'Overdue').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const nextDue = invoices.filter(i => (i.status === 'Pending' || i.status === 'Overdue') && i.due).sort((a, b) => new Date(a.due) - new Date(b.due))[0];
  const lastPaid = invoices.filter(i => i.status === 'Paid' && i.date).sort((a, b) => new Date(b.date) - new Date(a.date))[0];

  const pct = totalInvoiced > 0 ? Math.round((totalPaid / totalInvoiced) * 100) : 0;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
      {[
        { label: 'Total Invoiced', val: fmtAmt(totalInvoiced), color: '#111827', bg: '#f9fafb' },
        { label: 'Total Paid', val: fmtAmt(totalPaid), color: '#16a34a', bg: '#f0fdf4' },
        { label: 'Amount Due', val: fmtAmt(totalDue), color: totalDue > 0 ? '#d97706' : '#6b7280', bg: totalDue > 0 ? '#fffbeb' : '#f9fafb' },
      ].map(({ label, val, color, bg }) => (
        <div key={label} style={{ background: bg, border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, fontWeight: 500 }}>{label}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color }}>{val}</div>
        </div>
      ))}
      {overdue > 0 && (
        <div style={{ gridColumn: '1/-1', background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#e11d48', fontWeight: 600 }}>Overdue Amount</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#e11d48' }}>{fmtAmt(overdue)}</span>
        </div>
      )}
      <div style={{ gridColumn: '1/-1', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Payment Progress</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>{pct}% collected</span>
        </div>
        <div style={{ background: '#e5e7eb', borderRadius: 99, height: 6 }}>
          <div style={{ width: pct + '%', background: '#16a34a', height: 6, borderRadius: 99, transition: 'width 0.3s' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, color: '#6b7280' }}>
          <span>Last paid: <b style={{ color: '#111827' }}>{lastPaid ? `${fmtAmt(lastPaid.amount)} on ${lastPaid.date}` : '—'}</b></span>
          <span>Next due: <b style={{ color: nextDue ? (daysUntil(nextDue.due) < 0 ? '#e11d48' : '#d97706') : '#6b7280' }}>
            {nextDue ? `${fmtAmt(nextDue.amount)} · ${daysLabel(daysUntil(nextDue.due))}` : '—'}
          </b></span>
        </div>
      </div>
    </div>
  );
}

// ── Projects Tab ──────────────────────────────────────────────────────────
function ProjectsTab({ client, onChange }) {
  const [form, setForm] = useState({ title: '', desc: '', value: '', status: 'In Progress', start: '', end: '' });
  const [adding, setAdding] = useState(false);

  const add = () => {
    if (!form.title) return;
    onChange({ ...client, projects: [{ id: uid(), ...form, createdAt: new Date().toLocaleDateString('en-IN') }, ...client.projects] });
    setForm({ title: '', desc: '', value: '', status: 'In Progress', start: '', end: '' });
    setAdding(false);
  };

  const updateStatus = (id, status) => {
    onChange({ ...client, projects: client.projects.map(p => p.id === id ? { ...p, status } : p) });
  };

  const remove = (id) => onChange({ ...client, projects: client.projects.filter(p => p.id !== id) });

  const ST = { 'In Progress': { bg: '#eff6ff', color: '#2563eb' }, 'Completed': { bg: '#f0fdf4', color: '#16a34a' }, 'On Hold': { bg: '#fffbeb', color: '#d97706' }, 'Cancelled': { bg: '#fff1f2', color: '#e11d48' } };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>Projects ({client.projects.length})</span>
        <button className="d-btn d-btn-primary d-btn-sm" onClick={() => setAdding(true)}>+ Add Project</button>
      </div>
      {adding && (
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[['title','Project Title','text'],['value','Value (₹)','number'],['start','Start Date','date'],['end','Deadline','date']].map(([k,l,t]) => (
              <div key={k} className="d-fg"><div className="d-flabel">{l}</div>
                <input className="d-finput" type={t} value={form[k]} onChange={e => setForm(p => ({...p,[k]:e.target.value}))} />
              </div>
            ))}
            <div className="d-fg" style={{ gridColumn: '1/-1' }}><div className="d-flabel">Description</div>
              <textarea className="d-finput" rows={2} value={form.desc} onChange={e => setForm(p => ({...p,desc:e.target.value}))} />
            </div>
            <div className="d-fg"><div className="d-flabel">Status</div>
              <select className="d-finput" value={form.status} onChange={e => setForm(p => ({...p,status:e.target.value}))}>
                {['In Progress','Completed','On Hold','Cancelled'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="d-btn" onClick={() => setAdding(false)}>Cancel</button>
            <button className="d-btn d-btn-primary" onClick={add}>Save Project</button>
          </div>
        </div>
      )}
      {client.projects.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13, padding: '12px 0' }}>No projects yet.</div>}
      {client.projects.map(p => {
        const s = ST[p.status] || ST['In Progress'];
        return (
          <div key={p.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 14px', marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: '#111827', fontSize: 13 }}>{p.title}</div>
                {p.desc && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{p.desc}</div>}
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4, display: 'flex', gap: 12 }}>
                  {p.start && <span>Start: {p.start}</span>}
                  {p.end && <span>Due: {p.end}</span>}
                  {p.value && <span style={{ color: '#2563eb', fontWeight: 600 }}>₹{parseInt(p.value).toLocaleString('en-IN')}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                <select value={p.status} onChange={e => updateStatus(p.id, e.target.value)}
                  style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, border: '1px solid #e5e7eb', background: s.bg, color: s.color, fontWeight: 600, cursor: 'pointer' }}>
                  {['In Progress','Completed','On Hold','Cancelled'].map(st => <option key={st}>{st}</option>)}
                </select>
                <button className="d-btn d-btn-sm" style={{ color: '#e11d48', borderColor: '#fecdd3', padding: '3px 8px' }} onClick={() => remove(p.id)}>✕</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Invoices Tab ──────────────────────────────────────────────────────────
function InvoicesTab({ client, onChange }) {
  const [form, setForm] = useState({ num: '', desc: '', amount: '', date: '', status: 'Pending', due: '', paidOn: '' });
  const [adding, setAdding] = useState(false);

  const add = () => {
    if (!form.num && !form.desc) return;
    onChange({ ...client, invoices: [{ id: uid(), ...form }, ...client.invoices] });
    setForm({ num: '', desc: '', amount: '', date: '', status: 'Pending', due: '', paidOn: '' });
    setAdding(false);
  };

  const updateInvStatus = (id, status) => {
    onChange({
      ...client,
      invoices: client.invoices.map(i => i.id === id ? {
        ...i, status,
        paidOn: status === 'Paid' ? (i.paidOn || new Date().toLocaleDateString('en-IN')) : i.paidOn,
      } : i),
    });
  };

  const remove = (id) => onChange({ ...client, invoices: client.invoices.filter(i => i.id !== id) });

  const ST = { Paid: { bg: '#f0fdf4', color: '#16a34a' }, Pending: { bg: '#fffbeb', color: '#d97706' }, Overdue: { bg: '#fff1f2', color: '#e11d48' }, Cancelled: { bg: '#f3f4f6', color: '#6b7280' } };

  return (
    <div>
      <PaymentSummary client={client} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>Invoices ({client.invoices.length})</span>
        <button className="d-btn d-btn-primary d-btn-sm" onClick={() => setAdding(true)}>+ Add Invoice</button>
      </div>
      {adding && (
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[['num','Invoice #','text'],['desc','Description','text'],['amount','Amount (₹)','number'],['date','Invoice Date','date'],['due','Due Date','date']].map(([k,l,t]) => (
              <div key={k} className="d-fg"><div className="d-flabel">{l}</div>
                <input className="d-finput" type={t} value={form[k]} onChange={e => setForm(p => ({...p,[k]:e.target.value}))} />
              </div>
            ))}
            <div className="d-fg"><div className="d-flabel">Status</div>
              <select className="d-finput" value={form.status} onChange={e => setForm(p => ({...p,status:e.target.value}))}>
                {['Pending','Paid','Overdue','Cancelled'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="d-btn" onClick={() => setAdding(false)}>Cancel</button>
            <button className="d-btn d-btn-primary" onClick={add}>Save Invoice</button>
          </div>
        </div>
      )}
      {client.invoices.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13, padding: '8px 0' }}>No invoices yet.</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        {client.invoices.length > 0 && (
          <thead><tr style={{ borderBottom: '1px solid #e5e7eb' }}>
            {['Invoice #','Description','Amount','Invoice Date','Due Date','Paid On','Status',''].map(h => (
              <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: 11 }}>{h}</th>
            ))}
          </tr></thead>
        )}
        <tbody>
          {client.invoices.map(inv => {
            const s = ST[inv.status] || ST.Pending;
            const days = daysUntil(inv.due);
            return (
              <tr key={inv.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '8px', fontWeight: 600, color: '#2563eb' }}>{inv.num || '—'}</td>
                <td style={{ padding: '8px', color: '#374151' }}>{inv.desc}</td>
                <td style={{ padding: '8px', fontWeight: 700, color: '#111827' }}>{fmtAmt(inv.amount)}</td>
                <td style={{ padding: '8px', color: '#6b7280', fontSize: 12 }}>{inv.date || '—'}</td>
                <td style={{ padding: '8px', fontSize: 12 }}>
                  {inv.due ? (
                    <span style={{ color: inv.status !== 'Paid' && days !== null && days < 0 ? '#e11d48' : '#6b7280' }}>
                      {inv.due}
                      {inv.status !== 'Paid' && days !== null && (
                        <span style={{ marginLeft: 4, fontSize: 10, color: days < 0 ? '#e11d48' : days <= 3 ? '#d97706' : '#9ca3af' }}>
                          ({daysLabel(days)})
                        </span>
                      )}
                    </span>
                  ) : '—'}
                </td>
                <td style={{ padding: '8px', color: '#16a34a', fontSize: 12 }}>{inv.paidOn || (inv.status === 'Paid' ? 'Paid' : '—')}</td>
                <td style={{ padding: '8px' }}>
                  <select value={inv.status} onChange={e => updateInvStatus(inv.id, e.target.value)}
                    style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, border: '1px solid #e5e7eb', background: s.bg, color: s.color, fontWeight: 600, cursor: 'pointer' }}>
                    {['Pending','Paid','Overdue','Cancelled'].map(st => <option key={st}>{st}</option>)}
                  </select>
                </td>
                <td style={{ padding: '8px' }}>
                  <button className="d-btn d-btn-sm" style={{ color: '#e11d48', borderColor: '#fecdd3', padding: '3px 8px' }} onClick={() => remove(inv.id)}>✕</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Notes Tab ─────────────────────────────────────────────────────────────
function NotesTab({ client, onChange }) {
  const [text, setText] = useState('');
  const add = () => {
    if (!text.trim()) return;
    onChange({ ...client, notes: (client.notes ? client.notes + '\n\n' : '') + `[${new Date().toLocaleDateString('en-IN')}]\n${text.trim()}` });
    setText('');
  };
  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Notes & Communication Log</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <textarea className="d-finput" rows={2} placeholder="Add a note, call log, or update..." value={text} onChange={e => setText(e.target.value)} style={{ flex: 1 }} />
        <button className="d-btn d-btn-primary" style={{ alignSelf: 'flex-end' }} onClick={add}>Add</button>
      </div>
      {client.notes
        ? <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: '#374151', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, lineHeight: 1.6 }}>{client.notes}</pre>
        : <div style={{ color: '#9ca3af', fontSize: 13 }}>No notes yet.</div>
      }
    </div>
  );
}

// ── Client Profile Modal ──────────────────────────────────────────────────
const TABS = ['Overview', 'Projects', 'Invoices', 'Notes'];

function ClientProfile({ client, onClose, onUpdate, onDelete }) {
  const [tab, setTab] = useState('Overview');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...client });

  const save = () => { onUpdate(form); setEditing(false); };

  const totalInvoiced = client.invoices.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalPaid = client.invoices.filter(i => i.status === 'Paid').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalDue = totalInvoiced - totalPaid;

  return (
    <div className="d-modal-overlay open" onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 10, width: '96vw', maxWidth: 920, maxHeight: '95vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.14)', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px 0', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 8, background: avColor(client.id), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>{initials(client.name)}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#111827' }}>{client.name}
                {client.clientId && <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 8, fontWeight: 400 }}>{client.clientId}</span>}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                {client.company && <span>{client.company} · </span>}{client.email}{client.phone && <span> · {client.phone}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 4, background: STATUS_BG[client.status] || '#f3f4f6', color: STATUS_COLORS[client.status] || '#6b7280', fontWeight: 600 }}>{client.status}</span>
              <button className="d-btn d-btn-sm d-btn-primary" onClick={() => setEditing(true)}>Edit</button>
              <button className="d-btn d-btn-sm" style={{ color: '#e11d48', borderColor: '#fecdd3' }} onClick={() => { if (window.confirm('Delete this client?')) { onDelete(client.id); onClose(); } }}>Delete</button>
              <span className="d-close-btn" onClick={onClose}>&times;</span>
            </div>
          </div>
          {/* Stats */}
          <div style={{ display: 'flex', gap: 20, marginBottom: 12, flexWrap: 'wrap' }}>
            {[
              ['Projects', client.projects.length],
              ['Invoices', client.invoices.length],
              ['Invoiced', fmtAmt(totalInvoiced)],
              ['Paid', fmtAmt(totalPaid)],
              ['Due', fmtAmt(totalDue)],
            ].map(([l, v]) => (
              <div key={l} style={{ fontSize: 12 }}>
                <span style={{ color: '#6b7280' }}>{l}: </span>
                <b style={{ color: l === 'Due' && totalDue > 0 ? '#d97706' : '#111827' }}>{v}</b>
              </div>
            ))}
          </div>
          {/* Tabs */}
          <div style={{ display: 'flex' }}>
            {TABS.map(t => (
              <button key={t} onClick={() => setTab(t)} style={{ padding: '7px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: tab === t ? 600 : 400, color: tab === t ? '#2563eb' : '#6b7280', borderBottom: tab === t ? '2px solid #2563eb' : '2px solid transparent' }}>{t}</button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {tab === 'Overview' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[['Client ID', client.clientId||'—'],['Name', client.name],['Company', client.company||'—'],['Email', client.email],['Phone', client.phone||'—'],['City', client.city||'—'],['Service', client.svc],['Added', client.createdAt||'—'],['Status', client.status]].map(([l,v]) => (
                <div key={l} style={{ background: '#f9fafb', borderRadius: 6, padding: '10px 14px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3 }}>{l}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{v}</div>
                </div>
              ))}
            </div>
          )}
          {tab === 'Projects' && <ProjectsTab client={client} onChange={onUpdate} />}
          {tab === 'Invoices' && <InvoicesTab client={client} onChange={onUpdate} />}
          {tab === 'Notes' && <NotesTab client={client} onChange={onUpdate} />}
        </div>
      </div>

      {editing && (
        <div className="d-modal-overlay open" style={{ zIndex: 1100 }} onClick={e => e.target === e.currentTarget && setEditing(false)}>
          <div className="d-modal">
            <div className="d-modal-title">Edit Client <span className="d-close-btn" onClick={() => setEditing(false)}>&times;</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {form.clientId && (
                <div className="d-fg" style={{ gridColumn: '1/-1' }}>
                  <div className="d-flabel">Client ID</div>
                  <input className="d-finput" readOnly value={form.clientId} style={{ color: '#9ca3af', background: '#f9fafb' }} />
                </div>
              )}
              {[['name','Name','text'],['company','Company','text'],['email','Email','email'],['phone','Phone','text'],['city','City','text']].map(([k,l,t]) => (
                <div key={k} className="d-fg"><div className="d-flabel">{l}</div>
                  <input className="d-finput" type={t} value={form[k]||''} onChange={e => setForm(p => ({...p,[k]:e.target.value}))} />
                </div>
              ))}
              <div className="d-fg"><div className="d-flabel">Service</div>
                <select className="d-finput" value={form.svc} onChange={e => setForm(p => ({...p,svc:e.target.value}))}>
                  {['Web Dev','AI Training','Research','Multiple','Other'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="d-fg"><div className="d-flabel">Status</div>
                <select className="d-finput" value={form.status} onChange={e => setForm(p => ({...p,status:e.target.value}))}>
                  {['Active','Inactive','Pending','Completed'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button className="d-btn" style={{ flex: 1 }} onClick={() => setEditing(false)}>Cancel</button>
              <button className="d-btn d-btn-primary" style={{ flex: 1 }} onClick={save}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Clients Page ─────────────────────────────────────────────────────
export default function Clients() {
  const [clients, setClients] = useState(() => loadClients());
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', city: '', svc: 'Web Dev', status: 'Active' });

  useEffect(() => { saveClients(clients); }, [clients]);

  const updateClient = (updated) => {
    setClients(prev => prev.map(c => c.id === updated.id ? updated : c));
    if (selected?.id === updated.id) setSelected(updated);
  };
  const deleteClient = (id) => setClients(prev => prev.filter(c => c.id !== id));

  const addClient = () => {
    if (!form.name.trim()) return;
    const newClient = { ...EMPTY_CLIENT, ...form, id: uid(), clientId: 'CLT-' + Date.now().toString(36).toUpperCase().slice(-6), createdAt: new Date().toLocaleDateString('en-IN') };
    setClients(prev => [newClient, ...prev]);
    setShowAdd(false);
    setForm({ name: '', company: '', email: '', phone: '', city: '', svc: 'Web Dev', status: 'Active' });
  };

  const filtered = clients.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase()) || (c.email||'').toLowerCase().includes(search.toLowerCase()) || (c.company||'').toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'All' || c.status === filter || c.svc === filter;
    return matchSearch && matchFilter;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="d-finput" placeholder="Search clients..." style={{ width: 220 }} value={search} onChange={e => setSearch(e.target.value)} />
          <select className="d-finput" style={{ width: 140 }} value={filter} onChange={e => setFilter(e.target.value)}>
            {['All','Active','Inactive','Pending','Completed','Web Dev','AI Training','Research'].map(f => <option key={f}>{f}</option>)}
          </select>
          <span style={{ fontSize: 12, color: '#6b7280' }}>Total: <b style={{ color: '#111827' }}>{clients.length}</b></span>
        </div>
        <button className="d-btn d-btn-primary" onClick={() => setShowAdd(true)}>+ Add Client</button>
      </div>

      {filtered.length === 0 && <div style={{ color: '#9ca3af', textAlign: 'center', padding: 40, fontSize: 13 }}>No clients found.</div>}
      <div style={{ display: 'grid', gap: 8 }}>
        {filtered.map(c => {
          const totalPaid = c.invoices.filter(i => i.status === 'Paid').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
          const totalDue = c.invoices.filter(i => i.status === 'Pending' || i.status === 'Overdue').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
          return (
            <div key={c.id} className="d-client-card" style={{ cursor: 'pointer' }} onClick={() => setSelected(c)}>
              <div style={{ width: 40, height: 40, borderRadius: 6, background: avColor(c.id), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{initials(c.name)}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{c.name}
                  {c.clientId && <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 6 }}>{c.clientId}</span>}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>{c.company && <span>{c.company} · </span>}{c.email}</div>
                <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span className="d-tag tw" style={{ fontSize: 10 }}>{c.svc}</span>
                  <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 4, background: STATUS_BG[c.status]||'#f3f4f6', color: STATUS_COLORS[c.status]||'#6b7280', fontWeight: 600 }}>{c.status}</span>
                  {c.projects.length > 0 && <span style={{ fontSize: 10, color: '#6b7280' }}>{c.projects.length} project{c.projects.length > 1 ? 's' : ''}</span>}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                {totalPaid > 0 && <div style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>{fmtAmt(totalPaid)} paid</div>}
                {totalDue > 0 && <div style={{ fontSize: 11, color: '#d97706', fontWeight: 600 }}>{fmtAmt(totalDue)} due</div>}
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>Added {c.createdAt}</div>
              </div>
            </div>
          );
        })}
      </div>

      {showAdd && (
        <div className="d-modal-overlay open" onClick={e => e.target === e.currentTarget && setShowAdd(false)}>
          <div className="d-modal">
            <div className="d-modal-title">Add New Client <span className="d-close-btn" onClick={() => setShowAdd(false)}>&times;</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[['name','Client Name *','text'],['company','Company','text'],['email','Email','email'],['phone','Phone','text'],['city','City','text']].map(([k,l,t]) => (
                <div key={k} className="d-fg"><div className="d-flabel">{l}</div>
                  <input className="d-finput" type={t} value={form[k]} onChange={e => setForm(p => ({...p,[k]:e.target.value}))} />
                </div>
              ))}
              <div className="d-fg"><div className="d-flabel">Service</div>
                <select className="d-finput" value={form.svc} onChange={e => setForm(p => ({...p,svc:e.target.value}))}>
                  {['Web Dev','AI Training','Research','Multiple','Other'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="d-fg"><div className="d-flabel">Status</div>
                <select className="d-finput" value={form.status} onChange={e => setForm(p => ({...p,status:e.target.value}))}>
                  {['Active','Pending','Inactive','Completed'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button className="d-btn" style={{ flex: 1 }} onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="d-btn d-btn-primary" style={{ flex: 1 }} onClick={addClient}>Save Client</button>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <ClientProfile client={selected} onClose={() => setSelected(null)} onUpdate={updateClient} onDelete={deleteClient} />
      )}
    </div>
  );
}
